import { Dialog } from '@base-ui/react'
import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { Virtuoso } from 'react-virtuoso'
import { z } from 'zod'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Combobox,
	ComboboxComponents,
	type ComboboxComponentsNamespace,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem
} from '@/components/ui/combobox'
import { DataTable } from '@/components/ui/data-table'
import { DialogTrigger } from '@/components/ui/dialog'
import { DropdownContent, DropdownItem } from '@/components/ui/dropdown-atoms'
import {
	ChevronDownIcon,
	DotsHorizontalIcon,
	EyeIcon,
	SearchLgIcon,
	Settings01Icon,
	XCloseIcon
} from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { formatDate } from '@/dates'
import { useAuthStore } from '@/features/auth/store/store'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { EscalationReasonCell } from '@/features/luxe-submissions/components/escalation-reason-cell'
import { FilterLocationSelect } from '@/features/luxe-submissions/components/filter-location-select'
import { FilterVetterSelect } from '@/features/luxe-submissions/components/filter-vetter-select'
import { FinalReviewListingActivityDialog } from '@/features/luxe-submissions/components/final-review-listing-activity-dialog'
import { FinalScoreBadge } from '@/features/luxe-submissions/components/final-score-badge'
import { ReviewerNameLabel } from '@/features/luxe-submissions/components/reviewer-name-label'
import { SourceBadge } from '@/features/luxe-submissions/components/source-badge'
import { SourceFilterSelect } from '@/features/luxe-submissions/components/source-filter-select'
import { SubmissionTypeFilterSelect } from '@/features/luxe-submissions/components/submission-type-filter-select'
import { useClaimFinalReviewListing } from '@/features/luxe-submissions/queries/use-claim-final-review-listing'
import { useGetFinalReviewListings } from '@/features/luxe-submissions/queries/use-get-final-review-listings'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetReviewers } from '@/features/luxe-submissions/queries/use-get-reviewers'
import {
	type FinalReviewListing,
	FinalReviewStatus,
	SubmissionSource,
	SubmissionType
} from '@/features/luxe-submissions/types'
import {
	finalReviewStatusOptions,
	finalReviewStatusToBadgeStyle,
	finalReviewStatusToLabelTKey,
	getReviewerName,
	submissionTypeToLabelTKey,
	toArrayParam
} from '@/features/luxe-submissions/utils'
import type { Order, OrderDirection } from '@/helpers/types'
import { cn, debounce, errorHandler } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import {
	ROW_SELECTION_COLUMN_ID,
	useRowSelection
} from '@/hooks/use-row-selection'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

const FinalReviewSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	locations: z.string().optional(),
	reviewerIds: z.string().optional(),
	vetterIds: z.string().optional(),
	sources: SubmissionSource.optional(),
	submissionTypes: SubmissionType.optional(),
	statuses: FinalReviewStatus.optional(),
	search: z.string().optional(),
	assignedToMe: z.enum(['true']).optional(),
	page: z.string().optional(),
	perPage: z.string().optional()
})
const listingActivityHandle = Dialog.createHandle<{
	listingId: string
}>()

type SortColumn =
	| 'listingId'
	| 'location'
	| 'submissionType'
	| 'source'
	| 'score'
	| 'status'
	| 'reviewer'
	| 'vetter'

function LuxeSubmissionsFinalReview() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const user = useAuthStore((state) => state.user)
	const location = useLocation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		FinalReviewSearchParamsSchema
	)
	const currentPage = searchParams.page ? Number(searchParams.page) : 1
	const { isEnabled: isEscalationReasonEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-escalation-reason-in-list'
	)
	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)
	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})
	// Queries
	const { data: listingsData, isLoading: isLoadingListings } =
		useGetFinalReviewListings({
			page: currentPage,
			perPage: rowsPerPage,
			sortBy: searchParams.sortBy,
			sortOrder: searchParams.sortOrder,
			locations: toArrayParam(searchParams.locations),
			reviewerIds: toArrayParam(searchParams.reviewerIds),
			vetterIds: toArrayParam(searchParams.vetterIds),
			sources: toArrayParam(searchParams.sources),
			submissionTypes: toArrayParam(searchParams.submissionTypes),
			statuses: toArrayParam(searchParams.statuses),
			assignedToMe:
				searchParams.assignedToMe === 'true' ? true : undefined,
			search: searchParams.search
		})

	// Mutations
	const claimMutation = useClaimFinalReviewListing()

	// Row selection - only unassigned rows (no reviewer) are selectable
	const { rowSelectionColumn, selectionCount, resetSelection, selected } =
		useRowSelection<FinalReviewListing, 'id'>({
			idField: 'id',
			data: listingsData?.data,
			isSelectable: (listing) => !listing.assignedReviewerId,
			getDisabledReason: (listing) =>
				listing.assignedReviewerId
					? t(
							'luxe_submissions:already_assigned_select_disabled_reason'
						)
					: undefined
		})

	// Pagination
	const lastPage = listingsData ? listingsData.meta.lastPage : undefined
	reportTotal(listingsData?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({
			page: page === 1 ? undefined : String(page)
		})
	}

	// Filters
	const handleLocationChange = (locationId: string | null) => {
		setSearchParams({
			locations: locationId ?? undefined,
			page: undefined
		})
	}

	const handleVetterChange = (vetterId: string | null) => {
		setSearchParams({
			vetterIds: vetterId ?? undefined,
			page: undefined
		})
	}

	const handleReviewerChange = (reviewerId: string | null) => {
		setSearchParams({
			reviewerIds: reviewerId ?? undefined,
			page: undefined
		})
	}

	const handleSourceChange = (source: string | null) => {
		setSearchParams({
			sources: (source as SubmissionSource) ?? undefined,
			page: undefined
		})
	}

	const handleSubmissionTypeChange = (submissionType: string | null) => {
		setSearchParams({
			submissionTypes: (submissionType as SubmissionType) ?? undefined,
			page: undefined
		})
	}

	const handleReviewStatusChange = (status: string | null) => {
		setSearchParams({
			statuses: (status as FinalReviewStatus) ?? undefined,
			page: undefined
		})
	}

	// Debounced search by ID
	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				setSearchParams({
					search: value || undefined,
					page: undefined
				})
			}, 300),
		[setSearchParams]
	)

	const getListingDetailPath = useCallback(
		(listingId: string) =>
			routes.LUXE_SUBMISSIONS_LISTING_FINAL_REVIEW.handle.fullPath.replace(
				':id',
				listingId
			),
		[]
	)

	// Navigate to listing final review detail. The row's own link uses the path
	// directly; this stays for the hops that follow an action, like claiming.
	const handleViewListing = useCallback(
		(listingId: string) => {
			navigate(getListingDetailPath(listingId), {
				state: { fromSearch: location.search }
			})
		},
		[navigate, location.search, getListingDetailPath]
	)

	// Claim listing
	const handleClaim = useCallback(
		async (listing: FinalReviewListing) => {
			try {
				await claimMutation.mutateAsync([listing.id])
				handleViewListing(listing.id)
			} catch (error) {
				errorHandler(error)
			}
		},
		[claimMutation, handleViewListing]
	)

	// Claim all selected listings
	const handleClaimAll = useCallback(async () => {
		if (selected.length === 0) {
			return
		}
		try {
			await claimMutation.mutateAsync(selected)
			resetSelection()
		} catch (error) {
			errorHandler(error)
		}
	}, [claimMutation, selected, resetSelection])

	// Order state for DataTable
	const order = useMemo((): Order<SortColumn> | undefined => {
		if (!searchParams.sortBy) {
			return undefined
		}
		return {
			orderBy: searchParams.sortBy as SortColumn,
			orderDirection: searchParams.sortOrder as OrderDirection | undefined
		}
	}, [searchParams.sortBy, searchParams.sortOrder])

	const setOrder = (newOrder?: Order<SortColumn>) => {
		setSearchParams({
			sortBy: newOrder?.orderBy,
			sortOrder: newOrder?.orderDirection,
			page: undefined
		})
	}
	const hasFilters = Object.values(searchParams).some(Boolean)

	// Table columns
	const columns = useMemo(
		() => [
			rowSelectionColumn,
			{
				id: 'listingId' as const,
				header: t('luxe_submissions:listing_id_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => (
					<div className='flex flex-col'>
						<a
							className={cn(
								'truncate font-medium text-neutral-900 text-sm',
								dataItem.listingUrl && 'underline'
							)}
							href={dataItem.listingUrl ?? undefined}
							rel='noopener noreferrer'
							target='_blank'
							title={dataItem.airbnbListingId ?? dataItem.id}
						>
							#{dataItem.airbnbListingId ?? dataItem.id}
						</a>

						{dataItem.isEscalated && (
							<span className='text-danger-600 text-xs'>
								{t('luxe_submissions:escalated')}
							</span>
						)}
					</div>
				),
				size: 240,
				sortKey: 'listingId' as const
			},
			{
				id: 'location' as const,
				header: t('luxe_submissions:location_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) =>
					dataItem.location,
				size: 160,
				sortKey: 'location' as const
			},
			{
				id: 'submissionType' as const,
				header: t('luxe_submissions:submission_type_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) =>
					dataItem.submissionType ? (
						<span className='text-neutral-700 text-sm'>
							{t(
								submissionTypeToLabelTKey[
									dataItem.submissionType
								]
							)}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					),
				size: 210,
				sortKey: 'submissionType' as const
			},
			{
				id: 'submittedAt' as const,
				header: t('luxe_submissions:submitted_at_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => {
					return dataItem.queueEnteredAt === null ? (
						<span className='text-neutral-500 text-sm'>—</span>
					) : (
						<span className='text-neutral-700 text-sm'>
							{formatDate(dataItem.queueEnteredAt, 'PP')}
						</span>
					)
				},
				size: 165
			},
			{
				id: 'source' as const,
				header: t('luxe_submissions:source_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => (
					<SourceBadge source={dataItem.source} />
				),
				size: 160,
				sortKey: 'source' as const
			},
			{
				id: 'score' as const,
				header: t(
					isFinalScoreEnabled
						? 'luxe_submissions:final_score_column'
						: 'luxe_submissions:vv_score_column'
				),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => (
					<FinalScoreBadge
						blockersFailedCount={dataItem.blockersFailedCount}
						designScore={dataItem.visualVettingScore}
						finalScore={dataItem.finalScore}
					/>
				),
				size: 160,
				sortKey: 'score' as const
			},
			{
				id: 'vetter' as const,
				header: t('luxe_submissions:vetter_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => {
					if (dataItem.vetterId) {
						return (
							<ReviewerNameLabel
								firstName={dataItem.vetterFirstName}
								lastName={dataItem.vetterLastName}
							/>
						)
					}

					return <span className='text-neutral-500 text-sm'>—</span>
				},
				size: 240,
				sortKey: 'vetter' as const
			},
			...(isEscalationReasonEnabled
				? [
						{
							id: 'escalationReason' as const,
							header: t(
								'luxe_submissions:escalation_reason_column'
							),
							cell: ({
								dataItem
							}: {
								dataItem: FinalReviewListing
							}) =>
								dataItem.escalationNote ? (
									<EscalationReasonCell
										note={dataItem.escalationNote}
									/>
								) : (
									<span className='text-neutral-500 text-sm'>
										—
									</span>
								),
							size: 400
						}
					]
				: []),
			{
				id: 'status' as const,
				header: t('luxe_submissions:review_status_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => (
					<span
						className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${finalReviewStatusToBadgeStyle[dataItem.reviewStatus]}`}
					>
						{t(finalReviewStatusToLabelTKey[dataItem.reviewStatus])}
					</span>
				),
				size: 185,
				sortKey: 'status' as const
			},
			{
				id: 'reviewer' as const,
				header: t('luxe_submissions:reviewer_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => {
					// If already assigned, show the reviewer name
					if (
						dataItem.assignedReviewerId &&
						dataItem.assignedReviewerName
					) {
						return (
							<ReviewerNameLabel
								firstName={
									dataItem.assignedReviewerName.split(' ')[0]
								}
								isMe={
									String(dataItem.assignedReviewerId) ===
									String(user?.id)
								}
								lastName={
									dataItem.assignedReviewerName.split(' ')[1]
								}
							/>
						)
					}

					return (
						<ButtonLegacy
							className='px-0'
							disabled={claimMutation.isPending}
							onClick={() => handleClaim(dataItem)}
							size='sm'
							type='button'
							variant='tertiary'
						>
							{t('luxe_submissions:claim')}
						</ButtonLegacy>
					)
				},
				size: 240,
				sortKey: 'reviewer' as const
			},
			{
				id: 'action' as const,
				header: t('luxe_submissions:action_column'),
				cell: ({ dataItem }: { dataItem: FinalReviewListing }) => (
					<div className='flex items-center gap-3'>
						<Link
							aria-label={t('luxe_submissions:see_details')}
							className='flex cursor-pointer items-center justify-center text-neutral-500 hover:text-neutral-700'
							state={{ fromSearch: location.search }}
							title={t('luxe_submissions:see_details')}
							to={getListingDetailPath(dataItem.id)}
						>
							<EyeIcon size={20} />
						</Link>

						<DropdownMenuRoot>
							<DropdownMenuTrigger asChild>
								<button
									aria-label={t('common:actions')}
									className='flex cursor-pointer items-center justify-center text-neutral-500 hover:text-neutral-700'
									type='button'
								>
									<DotsHorizontalIcon size={20} />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuPortal>
								<DropdownContent
									align='end'
									className='w-50'
									sideOffset={4}
								>
									<DialogTrigger
										handle={listingActivityHandle}
										id='trigger-activity'
										nativeButton={false}
										payload={{
											listingId: dataItem.id
										}}
										render={
											<DropdownItem className='text-neutral-900'>
												{t(
													'luxe_submissions:show_activity'
												)}
											</DropdownItem>
										}
									/>
								</DropdownContent>
							</DropdownMenuPortal>
						</DropdownMenuRoot>
					</div>
				),
				size: 100
			}
		],
		[
			t,

			handleClaim,
			getListingDetailPath,
			location.search,
			claimMutation.isPending,
			rowSelectionColumn,
			user,
			isEscalationReasonEnabled,
			isFinalScoreEnabled
		]
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10'>
			{/* Filters */}
			<div className='flex shrink-0 items-center justify-between gap-3 py-6'>
				<div className='flex min-w-0 items-center gap-3'>
					<FilterLocationSelect
						onChange={handleLocationChange}
						selectedId={searchParams.locations ?? null}
					/>
					<SourceFilterSelect
						onChange={handleSourceChange}
						value={searchParams.sources ?? null}
					/>
					<SubmissionTypeFilterSelect
						onChange={handleSubmissionTypeChange}
						value={searchParams.submissionTypes ?? null}
					/>
					<FilterVetterSelect
						onChange={handleVetterChange}
						placeholder='Vetter'
						section='visual_vetting'
						selectedId={searchParams.vetterIds ?? null}
						userId={user?.id}
					/>
					<StatusFilterSelect
						onChange={handleReviewStatusChange}
						value={searchParams.statuses ?? null}
					/>
					<FilterVetterSelect
						onChange={handleReviewerChange}
						placeholder='Reviewer'
						section='final_review'
						selectedId={searchParams.reviewerIds ?? null}
						userId={user?.id}
					/>
				</div>
				<div className='relative w-[320px]'>
					<SearchLgIcon
						className='pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500'
						size={20}
					/>
					<Input
						className='h-9 w-full rounded-lg border-neutral-300 pl-10 text-sm shadow-xs placeholder:text-neutral-500'
						defaultValue={searchParams.search ?? ''}
						onChange={(e) => debouncedSearch(e.target.value)}
						placeholder={t(
							'luxe_submissions:search_by_listing_id_email'
						)}
					/>
				</div>
			</div>

			{/* Selection bar */}
			{selectionCount > 0 && (
				<div className='mb-4 flex items-center justify-between rounded-lg border border-neutral-200 bg-primary-50 px-6 py-3'>
					<span className='font-medium text-neutral-600 text-xs'>
						{selectionCount} Selected
					</span>
					<div className='flex items-center gap-6'>
						<ButtonLegacy
							disabled={claimMutation.isPending}
							onClick={handleClaimAll}
							size='sm'
							type='button'
							variant='outline'
						>
							{t('luxe_submissions:claim_all')}
							{claimMutation.isPending && <Spinner />}
						</ButtonLegacy>

						<button
							className='cursor-pointer font-medium text-neutral-900 text-sm underline'
							onClick={resetSelection}
							type='button'
						>
							Clear
						</button>
					</div>
				</div>
			)}

			{/* Table */}
			<div className='flex-1 overflow-hidden'>
				<DataTable
					columnPinning={{
						left: [ROW_SELECTION_COLUMN_ID],
						right: ['action']
					}}
					columns={columns}
					data={listingsData?.data ?? []}
					emptyComponent={
						<EmptyStateListings
							action={
								hasFilters ? null : (
									<ButtonLegacy
										onClick={() => window.location.reload()}
										variant='secondary'
									>
										{t('common:refresh')}
									</ButtonLegacy>
								)
							}
							description={t(
								hasFilters
									? 'common:empty_table_description'
									: 'luxe_submissions:final_review_no_listings_description'
							)}
							title={t(
								hasFilters
									? 'common:empty_table_title'
									: 'luxe_submissions:final_review_no_listings_title'
							)}
						/>
					}
					isLoading={isLoadingListings}
					lastPage={lastPage}
					order={order}
					page={currentPage}
					rowsPerPage={rowsPerPage}
					setOrder={setOrder}
					setPage={setPage}
					setRowsPerPage={setRowsPerPage}
					totalRows={totalRows}
				/>
			</div>

			<FinalReviewListingActivityDialog handle={listingActivityHandle} />
		</div>
	)
}

// Status filter select component
interface StatusFilterSelectProps {
	value: string | null
	onChange: (value: string | null) => void
}

function StatusFilterSelect({ value, onChange }: StatusFilterSelectProps) {
	const { t } = useTranslation()
	const selectedOption = finalReviewStatusOptions.find(
		(opt) => opt.value === value
	)

	const handleValueChange = (newValue: string | null) => {
		// Toggle off if same value selected
		onChange(newValue === value ? null : newValue)
	}

	return (
		<Select modal onValueChange={handleValueChange} value={value}>
			<SelectTrigger
				className='h-10 min-w-0 gap-2 border-neutral-300 bg-neutral-white px-3 text-neutral-700 text-sm shadow-none hover:shadow-none data-popup-open:shadow-none [&>svg]:text-neutral-500'
				nativeButton={false}
				size='sm'
			>
				<SelectValue className='min-w-0'>
					<span className='flex min-w-0 items-center gap-2'>
						<Settings01Icon className='shrink-0' size={20} />
						{selectedOption ? (
							<>
								<span className='truncate'>
									{t(selectedOption.label)}
								</span>
								<button
									className='flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700'
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation()
										e.preventDefault()

										onChange(null)
									}}
									onPointerDown={(e: React.MouseEvent) => {
										e.stopPropagation()
										e.preventDefault()
									}}
									type='button'
								>
									<XCloseIcon size={14} />
								</button>
							</>
						) : (
							<span className='truncate font-medium text-neutral-900'>
								{t('luxe_submissions:status_column')}
							</span>
						)}
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent className='w-(--anchor-width) min-w-50'>
				{finalReviewStatusOptions.map((option) => (
					<SelectItem
						className='whitespace-normal!'
						key={option.value}
						value={option.value}
					>
						{t(option.label)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

// Reviewer select component - used in table cells and bulk assign
interface ReviewerSelectProps {
	onSelect?: (reviewerId: string) => void
	variant: 'cell' | 'bulk'
}

export function ReviewerSelect({ onSelect, variant }: ReviewerSelectProps) {
	const { t } = useTranslation()
	const [reviewerSearch, setReviewerSearch] = useState('')
	const {
		remappedData: reviewers,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage
	} = useGetReviewers('final_review', reviewerSearch)
	const lastSearchRef = useRef('')
	const debouncedSearch = useMemo(
		() =>
			debounce((val: string) => {
				if (val !== lastSearchRef.current) {
					lastSearchRef.current = val
					setReviewerSearch(val)
				}
			}, 300),
		[]
	)

	const handleValueChange = (
		reviewerId: string | null,
		_eventDetails: ComboboxComponentsNamespace.Root.ChangeEventDetails
	) => {
		if (reviewerId) {
			onSelect?.(reviewerId)
		}
	}

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className='flex flex-col gap-1 p-1'>
					<Skeleton height={36} width='100%' />
					<Skeleton height={36} width='100%' />
					<Skeleton height={36} width='100%' />
				</div>
			)
		}

		if (reviewers.length === 0) {
			return (
				<div className='p-2 text-center text-neutral-500 text-sm'>
					No reviewers found
				</div>
			)
		}

		return (
			<Virtuoso
				className='h-50! [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
				computeItemKey={(_, item) => item.id}
				data={reviewers}
				endReached={() => {
					if (hasNextPage && !isFetchingNextPage) {
						fetchNextPage()
					}
				}}
				itemContent={(_, reviewer) => {
					const { fullName, initials } = getReviewerName(reviewer)
					return (
						<ComboboxItem value={reviewer.id}>
							<div className='flex items-center gap-2'>
								<div className='flex h-6 w-6 items-center justify-center rounded-full bg-neutral-200 font-medium text-neutral-700 text-xs'>
									{initials}
								</div>
								{fullName}
							</div>
						</ComboboxItem>
					)
				}}
			/>
		)
	}

	const popoverWidth = variant === 'cell' ? 'w-[240px]' : 'w-[320px]'

	return (
		<Combobox
			modal
			onInputValueChange={(inputValue: string) =>
				debouncedSearch(inputValue)
			}
			onValueChange={handleValueChange}
			value={null}
		>
			{variant === 'cell' ? (
				<ComboboxComponents.Trigger
					className={cn(
						'flex h-auto cursor-pointer items-center gap-2 border-none bg-transparent text-neutral-700 text-sm shadow-none outline-none',
						'hover:bg-transparent focus:shadow-none'
					)}
				>
					{t('luxe_submissions:assign_task')}
					<ComboboxComponents.Icon className='text-neutral-500'>
						<ChevronDownIcon
							className='in-data-popup-open:rotate-180 transition-transform duration-150'
							size={16}
						/>
					</ComboboxComponents.Icon>
				</ComboboxComponents.Trigger>
			) : (
				<ComboboxComponents.Trigger
					className={cn(
						'flex h-10 w-[320px] cursor-pointer items-center gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3.5 text-base text-neutral-500 shadow-xs',
						'in-data-popup-open:shadow-focus-primary-100-xxs in-data-popup-open:outline in-data-popup-open:outline-neutral-300'
					)}
				>
					Select reviewer to assign task
					<ComboboxComponents.Icon className='ml-auto text-neutral-500'>
						<ChevronDownIcon
							className='in-data-popup-open:rotate-180 transition-transform duration-150'
							size={16}
						/>
					</ComboboxComponents.Icon>
				</ComboboxComponents.Trigger>
			)}
			<ComboboxContent className={popoverWidth}>
				<ComboboxInput placeholder={t('luxe_submissions:search')} />
				<div className='max-h-50 overflow-hidden'>
					{renderContent()}
					{isFetchingNextPage && (
						<div className='p-1'>
							<Skeleton height={36} width='100%' />
						</div>
					)}
				</div>
			</ComboboxContent>
		</Combobox>
	)
}

export { LuxeSubmissionsFinalReview }
